import { useQuery } from '@tanstack/react-query';

import React, { useMemo, useState } from 'react';

import { Close, Groups, Hexagon, PlayArrow, VpnKey } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormControlLabel, IconButton, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { CopyToClipboardButton } from '@@components';

import { ApiKeyModel, ClientModel, ComponentModel, ComponentRuntimeProfile, EnvironmentClient, FlowClient, RuntimeProfileTier, SetComponentDependenciesRequest } from '../../../api/api';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import AddOperationDependencyDialog from './AddOperationDependencyDialog';
import AttributesList from './AttributesList';
import { COMPONENTS_PANEL_COLLAPSED_WIDTH, COMPONENTS_PANEL_DEFAULT_WIDTH } from './ComponentsPanel';
import DependencyTree from './DependencyTree';
import { ReferencingResourcesSection } from './ReferencingResourceCard';
import SectionWithAction from './SectionWithAction';

const COMPONENT_DETAILS_PANEL_STORAGE_KEY = 'componentDetailsPanel.width';
const COMPONENT_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const COMPONENT_DETAILS_PANEL_MIN_WIDTH = 360;
const COMPONENT_DETAILS_PANEL_MAX_WIDTH = 1000;
const DEFAULT_RUNTIME_PROFILE: ComponentRuntimeProfile = {
	tier: RuntimeProfileTier.Small,
	minInstances: 0,
	maxInstances: 3,
};

interface ComponentDetailsPanelProps {
	componentId: string;
	components?: ComponentModel[];
	clients?: ClientModel[];
	apiKeys?: ApiKeyModel[];
	onClose: () => void;
	onRefresh?: () => void;
	onClientSelect?: (clientId: string) => void;
	onApiKeySelect?: (apiKeyId: string) => void;
	listPanelCollapsed?: boolean;
	rightOffset?: number;
	top?: number;
}

export default function ComponentDetailsPanel({
	componentId,
	components = [],
	clients = [],
	apiKeys = [],
	onClose,
	onRefresh,
	onClientSelect,
	onApiKeySelect,
	listPanelCollapsed = false,
	rightOffset = COMPONENTS_PANEL_DEFAULT_WIDTH,
	top = 0,
}: ComponentDetailsPanelProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const flowClient = useApiClient(FlowClient) as FlowClient;
	const toastService = useToastService();
	const [refreshKey, setRefreshKey] = useState(0);
	const [openAddDependencyDialog, setOpenAddDependencyDialog] = useState(false);
	const [deletingDependencyId, setDeletingDependencyId] = useState<string | null>(null);
	const [panelWidth, setPanelWidth] = useState(COMPONENT_DETAILS_PANEL_DEFAULT_WIDTH);
	const [runtimeProfile, setRuntimeProfile] = useState<ComponentRuntimeProfile>(DEFAULT_RUNTIME_PROFILE);
	const [isRuntimeProfileOverrideEnabled, setIsRuntimeProfileOverrideEnabled] = useState(false);
	const [isSavingRuntimeProfile, setIsSavingRuntimeProfile] = useState(false);

	// Find the component from the passed components
	const component = useMemo(() => {
		return components.find((c) => c.id === componentId);
	}, [components, componentId]);

	React.useEffect(() => {
		const profile = component?.runtimeProfile ?? DEFAULT_RUNTIME_PROFILE;
		setRuntimeProfile(profile);
		setIsRuntimeProfileOverrideEnabled(component?.runtimeProfileOverride != null);
	}, [component?.id, component?.runtimeProfile, component?.runtimeProfileOverride]);

	const isLoading = false;
	const isFetching = false;
	const error = null;

	const handleRefresh = () => {
		setRefreshKey((prev) => prev + 1);
		if (onRefresh) {
			onRefresh();
		}
	};

	const handleAddDependency = () => {
		setOpenAddDependencyDialog(true);
	};

	const handleDependencyAdded = () => {
		setOpenAddDependencyDialog(false);
		handleRefresh();
	};

	const handleCloseAddDependencyDialog = () => {
		setOpenAddDependencyDialog(false);
	};

	const handleDeleteDependency = async (dependencyId: string) => {
		if (!component) return;

		setDeletingDependencyId(dependencyId);
		try {
			const request: SetComponentDependenciesRequest = {
				remove: [dependencyId],
			};

			await client.setDependencies(componentId, request);
			toastService.success('Operation dependency removed successfully');
			handleRefresh();
		} catch (error) {
			console.error('Error removing operation dependency:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove dependency: ${errorMessage}`);
		} finally {
			setDeletingDependencyId(null);
		}
	};

	const handleDeleteAttributes = async (keys: string[]) => {
		if (!component || keys.length === 0) return;

		try {
			// Delete all selected attributes
			for (const key of keys) {
				await client.removeAttribute(componentId, key);
			}

			toastService.success(`${keys.length} attribute${keys.length !== 1 ? 's' : ''} removed successfully`);
			handleRefresh();
		} catch (error) {
			console.error('Error removing attributes:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove attributes: ${errorMessage}`);
			throw error;
		}
	};

	const handleSaveCustomAttributes = async (editedAttributes: { [key: string]: string }) => {
		if (!component) return;

		try {
			// Get current custom attributes
			const currentCustomAttrs: { [key: string]: string } = {};
			if (component.environmentVariables) {
				Object.entries(component.environmentVariables).forEach(([key, value]) => {
					if (key.startsWith('Custom:')) {
						currentCustomAttrs[key] = String(value);
					}
				});
			}

			// Determine which attributes were deleted
			const deletedKeys = Object.keys(currentCustomAttrs).filter((key) => !(key in editedAttributes));

			// Delete removed attributes
			for (const key of deletedKeys) {
				await client.removeAttribute(componentId, key);
			}

			const changeCount = deletedKeys.length;
			if (changeCount > 0) {
				toastService.success(`Custom attributes updated successfully (${changeCount} deletion${changeCount !== 1 ? 's' : ''}).`);
			} else {
				toastService.info('No changes to save.');
			}

			handleRefresh();
		} catch (error) {
			console.error('Error saving custom attributes:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to save custom attributes: ${errorMessage}`);
			throw error; // Re-throw to keep edit mode open
		}
	};

	const handleSaveRuntimeProfile = async () => {
		if (isRuntimeProfileOverrideEnabled && runtimeProfile.minInstances > runtimeProfile.maxInstances) {
			toastService.error('Minimum instances cannot exceed maximum instances.');
			return;
		}

		try {
			setIsSavingRuntimeProfile(true);
			await client.setComponentRuntimeProfile(componentId, {
				runtimeProfileOverride: isRuntimeProfileOverrideEnabled ? runtimeProfile : undefined,
			});
			toastService.success('Default runtime profile saved. Existing environments use it on their next deployment unless overridden.');
			handleRefresh();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to save default runtime profile: ${errorMessage}`);
		} finally {
			setIsSavingRuntimeProfile(false);
		}
	};

	// Filter dependencies by TargetType = "Operation"
	const operationDependencies = useMemo(() => {
		if (!component?.dependencies) return [];
		return component.dependencies.filter((dep) => dep.targetType === 'Operation');
	}, [component?.dependencies]);

	// Query to fetch operation details for all dependencies
	const { data: operationDetails } = useQuery({
		queryKey: ['operation-details', operationDependencies.map((d) => d.targetId).join(','), refreshKey],
		queryFn: async () => {
			if (!components || operationDependencies.length === 0) return {};

			const details: Record<string, { name: string; contextId: string }> = {};

			// Fetch operation details for each dependency
			await Promise.all(
				operationDependencies.map(async (dep) => {
					try {
						// Find the component that contains this operation
						const depComponent = components.find((c) => c.id === dep.dependsOnComponentId);
						if (!depComponent?.contextId) return;

						// Fetch operation metadata
						const operation = await flowClient.getOperationById(depComponent.contextId, dep.targetId, false);
						details[dep.targetId] = {
							name: operation.name,
							contextId: operation.contextId,
						};
					} catch (error) {
						console.error(`Failed to fetch operation ${dep.targetId}:`, error);
					}
				}),
			);

			return details;
		},
		enabled: !!components && operationDependencies.length > 0,
	});

	// Group dependencies by component
	const groupedDependencies = useMemo(() => {
		if (!components || !operationDetails) return [];

		const groups: Array<{
			componentId: string;
			componentName: string;
			dependencies: Array<{
				id: string;
				targetId: string;
				operationName: string;
				createdDate: string;
			}>;
		}> = [];

		// Group by dependsOnComponentId
		const grouped = new Map<string, typeof operationDependencies>();
		operationDependencies.forEach((dep) => {
			if (!grouped.has(dep.dependsOnComponentId)) {
				grouped.set(dep.dependsOnComponentId, []);
			}
			grouped.get(dep.dependsOnComponentId)!.push(dep);
		});

		// Build the final structure
		grouped.forEach((deps, componentId) => {
			const comp = components.find((c) => c.id === componentId);
			if (!comp) return;

			const dependencies = deps.map((dep) => ({
				id: dep.id,
				targetId: dep.targetId,
				operationName: operationDetails[dep.targetId]?.name || dep.targetId,
				createdDate: dep.createdDate,
			}));

			// Sort operations by name
			dependencies.sort((a, b) => a.operationName.localeCompare(b.operationName));

			groups.push({
				componentId,
				componentName: comp.name,
				dependencies,
			});
		});

		// Sort components by name
		groups.sort((a, b) => a.componentName.localeCompare(b.componentName));

		return groups;
	}, [components, operationDependencies, operationDetails]);

	const referencingClients = useMemo(
		() =>
			clients
				.map((item) => ({
					item,
					dependencies: item.dependencies?.filter((dependency) => dependency.dependsOnComponentId === componentId) ?? [],
				}))
				.filter((item) => item.dependencies.length > 0)
				.sort((left, right) => left.item.name.localeCompare(right.item.name)),
		[clients, componentId],
	);

	const referencingApiKeys = useMemo(
		() =>
			apiKeys
				.map((item) => ({
					item,
					dependencies: item.dependencies?.filter((dependency) => dependency.dependsOnComponentId === componentId) ?? [],
				}))
				.filter((item) => item.dependencies.length > 0)
				.sort((left, right) => left.item.name.localeCompare(right.item.name)),
		[apiKeys, componentId],
	);

	// Subtitle component with ID and copy button
	const subtitle = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1, flexShrink: 1 }}>
				ID: {componentId}
			</Typography>
			<CopyToClipboardButton value={componentId} size="small" iconSize={14} title="Copy ID to clipboard" edge={false} sx={{ p: 0.5 }} />
		</Box>
	);

	return (
		<>
			<SidePanelDetail
				title="Component Details"
				subtitle={subtitle}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error as Error | null}
				errorMessage="Failed to load component details"
				onClose={onClose}
				onRefresh={handleRefresh}
				position="stacked"
				top={top}
				rightOffset={rightOffset}
				width={panelWidth}
				zIndex={1201}
				listPanelCollapsed={listPanelCollapsed}
				listPanelCollapsedWidth={COMPONENTS_PANEL_COLLAPSED_WIDTH}
				resizeStorageKey={COMPONENT_DETAILS_PANEL_STORAGE_KEY}
				defaultWidth={COMPONENT_DETAILS_PANEL_DEFAULT_WIDTH}
				minWidth={COMPONENT_DETAILS_PANEL_MIN_WIDTH}
				maxWidth={COMPONENT_DETAILS_PANEL_MAX_WIDTH}
				setWidth={setPanelWidth}
			>
				{component && (
					<>
						<Box sx={{ mb: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Name:
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 500 }}>
									{component.name}
								</Typography>
							</Stack>

							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Container Image:
								</Typography>
								<Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
									{component.containerImageName}
								</Typography>
							</Stack>

							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="body2" color="textSecondary">
									Container Version:
								</Typography>
								<Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
									{component.containerImageVersion}
								</Typography>
							</Stack>

							{component.contextId && (
								<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
									<Typography variant="body2" color="textSecondary">
										Context ID:
									</Typography>
									<Stack direction="row" spacing={1} alignItems="center">
										<Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
											{component.contextId}
										</Typography>
										<CopyToClipboardButton value={component.contextId} size="small" iconSize={14} title="Copy Context ID to clipboard" edge={false} />
									</Stack>
								</Stack>
							)}
						</Box>

						<Divider sx={{ my: 2 }} />

						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
								Default runtime profile
							</Typography>
							<Stack spacing={1.5}>
								<FormControlLabel
									control={
										<Checkbox
											checked={isRuntimeProfileOverrideEnabled}
											onChange={(event) => {
												setIsRuntimeProfileOverrideEnabled(event.target.checked);
												if (!event.target.checked) {
													setRuntimeProfile(DEFAULT_RUNTIME_PROFILE);
												}
											}}
										/>
									}
									label="Override platform default"
								/>
								<Select
									size="small"
									value={runtimeProfile.tier}
									disabled={!isRuntimeProfileOverrideEnabled}
									onChange={(event) => setRuntimeProfile((current) => ({ ...current, tier: event.target.value as RuntimeProfileTier }))}
									inputProps={{ 'aria-label': 'Default runtime profile tier' }}
								>
									{Object.values(RuntimeProfileTier).map((tier) => (
										<MenuItem key={tier} value={tier}>
											{tier}
										</MenuItem>
									))}
								</Select>
								<Stack direction="row" spacing={1.5}>
									<TextField
										fullWidth
										size="small"
										type="number"
										label="Min instances"
										value={runtimeProfile.minInstances}
										disabled={!isRuntimeProfileOverrideEnabled}
										onChange={(event) => setRuntimeProfile((current) => ({ ...current, minInstances: Math.max(0, Number(event.target.value)) }))}
										inputProps={{ min: 0, step: 1 }}
									/>
									<TextField
										fullWidth
										size="small"
										type="number"
										label="Max instances"
										value={runtimeProfile.maxInstances}
										disabled={!isRuntimeProfileOverrideEnabled}
										onChange={(event) => setRuntimeProfile((current) => ({ ...current, maxInstances: Math.max(0, Number(event.target.value)) }))}
										inputProps={{ min: 0, step: 1 }}
									/>
								</Stack>
								<Stack direction="row" justifyContent="flex-end">
									<Button size="small" variant="contained" onClick={handleSaveRuntimeProfile} disabled={isSavingRuntimeProfile}>
										Save
									</Button>
								</Stack>
							</Stack>
						</Box>

						{/* Operation Dependencies */}
						<SectionWithAction
							title="Operation Dependencies"
							count={operationDependencies.length}
							onAdd={handleAddDependency}
							addButtonTitle="Add operation dependency"
							emptyMessage="No operation dependencies found for this component"
						>
							<DependencyTree
								root={{
									id: component.id,
									label: component.name,
									icon: <Hexagon sx={{ fontSize: 16, color: 'primary.main' }} />,
									children: groupedDependencies.map((group) => ({
										id: group.componentId,
										label: group.componentName,
										icon: <Hexagon sx={{ fontSize: 14, color: 'primary.main' }} />,
										children: group.dependencies.map((dependency) => ({
											id: dependency.id,
											label: dependency.operationName,
											icon: <PlayArrow sx={{ fontSize: 16, color: 'primary.main' }} />,
											action: (
												<Tooltip title="Remove dependency">
													<span>
														<IconButton
															size="small"
															onClick={() => handleDeleteDependency(dependency.targetId)}
															disabled={deletingDependencyId === dependency.targetId}
															color="error"
														>
															<Close fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
											),
										})),
									})),
								}}
							/>
						</SectionWithAction>

						<Divider sx={{ my: 2 }} />

						<ReferencingResourcesSection
							title="Referencing API Clients"
							emptyMessage="No API clients refer to this component"
							items={referencingClients.map(({ item, dependencies }) => ({
								id: item.id,
								icon: <Groups sx={{ fontSize: 16, color: 'primary.main' }} />,
								name: item.name,
								code: item.code,
								dependencies: dependencies.map((dependency) => dependency.targetType),
								onOpen: onClientSelect ? () => onClientSelect(item.id) : undefined,
								openTitle: 'Open client',
							}))}
						/>

						<Divider sx={{ my: 2 }} />

						<ReferencingResourcesSection
							title="Referencing API Keys"
							emptyMessage="No API keys refer to this component"
							items={referencingApiKeys.map(({ item, dependencies }) => ({
								id: item.id,
								icon: <VpnKey sx={{ fontSize: 16, color: 'primary.main' }} />,
								name: item.name,
								code: item.code,
								dependencies: dependencies.map((dependency) => `${dependency.accessChannel} ${dependency.targetType}`),
								onOpen: onApiKeySelect ? () => onApiKeySelect(item.id) : undefined,
								openTitle: 'Open API key',
							}))}
						/>

						<Divider sx={{ my: 2 }} />

						<AttributesList
							attributes={component.environmentVariables}
							title="Environment Variables"
							emptyMessage="No environment variables defined for this component"
							onSaveCustomAttributes={handleSaveCustomAttributes}
							onDeleteAttributes={handleDeleteAttributes}
						/>
					</>
				)}
			</SidePanelDetail>

			{/* Add Operation Dependency Dialog */}
			{component && (
				<AddOperationDependencyDialog
					open={openAddDependencyDialog}
					componentId={componentId}
					componentName={component.name}
					onClose={handleCloseAddDependencyDialog}
					onDependencyAdded={handleDependencyAdded}
				/>
			)}
		</>
	);
}
